<script lang="ts" setup>
import type { DropdownMenuItem } from '@nuxt/ui';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useAccessDisplayNames, useAuth } from '#layers/access/public';
import { useUserMenuItems } from '~/composables/use-user-menu';

interface Props {
	// `avatar` is a bare avatar trigger (sidebar); `expanded` is the labelled avatar + name/email
	// + chevron button used where there's room for it (onboarding / no-workspace chrome).
	variant?: 'avatar' | 'expanded';
}

const { variant = 'avatar' } = defineProps<Props>();

const { currentUser } = storeToRefs(useAuth());
const { getUserDisplayName } = useAccessDisplayNames();

const commonItems = useUserMenuItems();

// The expanded trigger already shows name/email, so it skips the in-menu identity header.
const items = computed<DropdownMenuItem[]>(() => variant === 'expanded'
	? commonItems.value
	: [{ slot: 'user', type: 'label' }, { type: 'separator' }, ...commonItems.value]);

const content = computed(() => variant === 'expanded'
	? { side: 'bottom' as const, align: 'end' as const }
	: { side: 'right' as const, align: 'end' as const });
</script>

<template>
	<DDropdownMenu
		v-if="currentUser"
		:content
		:items
	>
		<DButton
			v-if="variant === 'expanded'"
			variant="ghost"
			color="neutral"
			trailing-icon="lucide:chevron-down"
			:ui="{
				base: 'group gap-2 select-none',
				trailingIcon: 'transition-transform duration-200 group-data-[state=open]:rotate-180',
			}"
		>
			<template #leading>
				<UserAvatar :user="currentUser" class="size-8" />
			</template>
			<div class="hidden flex-col items-start sm:flex">
				<span class="text-sm font-semibold">{{ getUserDisplayName(currentUser) }}</span>
				<span v-if="currentUser.fullName" class="text-xs text-muted">{{ currentUser.email }}</span>
			</div>
		</DButton>

		<button
			v-else
			type="button"
			class="flex size-8 cursor-pointer items-center justify-center rounded-full"
			:aria-label="getUserDisplayName(currentUser)"
		>
			<UserAvatar :user="currentUser" class="size-8" />
		</button>

		<template #user-label>
			<span class="inline-flex flex-col items-start">
				<span class="text-sm font-semibold">
					{{ getUserDisplayName(currentUser) }}
				</span>
				<span v-if="currentUser.fullName" class="text-xs text-muted">{{ currentUser.email }}</span>
			</span>
		</template>

		<template #user-leading>
			<UserAvatar
				:user="currentUser"
				class="size-8 text-[10px]"
				:ui="{
					root: 'bg-(--ui-text)',
					fallback: 'text-inverted',
				}"
			/>
		</template>
	</DDropdownMenu>
</template>
